import React, { FC, useRef, useState } from 'react'
import { Swiper } from 'swiper/react'
import styled, { css } from 'styled-components'
import 'swiper/css'
import 'swiper/css/pagination'
import { Box, Flex } from '@/components/atoms/Grid'
import ProductSliderArrow, { ArrowActions } from './productSliderArrow'
import { ArrowDirection } from '@/components/molecules/arrows'
import type { Swiper as SwiperClass } from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import useTimer from 'hooks/useTimer'

export enum SliderDirection {
  vertical,
  horizontal
}

export enum ArrowsPosition {
  Inside,
  Outside
}

type ProductsSliderProps = {
  slidesToShow?: number | string
  sliderDirection?: SliderDirection
  spaceBetween?: number
  showPagination?: boolean
  autoPlay?: boolean | SliderAutoPlay
  infinite?: boolean
  arrowsTopPercent?: number
  arrowsBottomMargin?: number
  slidesPerGroup?: number | { desktop: number; tablet: number; mobile: number }
  breakpoints?: SliderBreakpoints
  arrowsPosition?: ArrowsPosition
  slideHeightPercent?: number
  showArrows?: boolean | 'auto'
  fillArrowBackground?: boolean
  pauseOnTouchMove?: boolean
  isHomePageBanner?: boolean
  onSwiper?: (swiper: SwiperClass) => void
} & Rest

type SliderBreakpoints = Record<
  number,
  {
    slidesPerView?: number | string
    slidesPerGroup?: number
  }
>

interface SliderAutoPlay {
  delay: number
  disableOnInteraction: boolean
  pauseOnMouseEnter: boolean
}

const ProductsSlider: FC<ProductsSliderProps> = ({
  slidesToShow = 'auto',
  slidesPerGroup = 1,
  sliderDirection = SliderDirection.horizontal,
  spaceBetween = 0,
  showPagination = false,
  autoPlay,
  arrowsTopPercent,
  arrowsBottomMargin,
  infinite = false,
  breakpoints,
  arrowsPosition = ArrowsPosition.Inside,
  slideHeightPercent,
  showArrows: initShowArrows = 'auto',
  fillArrowBackground = true,
  children,
  pauseOnTouchMove,
  isHomePageBanner,
  onSwiper: _onSwiper,
  ...rest
}) => {
  const nextArrowRef = useRef<ArrowActions>(null)
  const prevArrowRef = useRef<ArrowActions>(null)
  const [swiper, setSwiper] = useState<SwiperClass>(null)
  const [showArrows] = useState(
    (initShowArrows === 'auto' &&
      typeof slidesToShow !== 'string' &&
      slidesToShow < React.Children.count(children)) ||
      initShowArrows
  )
  let autoplayTimeout

  const refreshSwiper = (swiper: SwiperClass) => {
    if (swiper) {
      swiper.update()
      onActiveIndexChange(swiper)
    }
  }

  useTimer(() => refreshSwiper(swiper), 200, [swiper])
  useTimer(() => refreshSwiper(swiper), 2000, [swiper])

  const pauseAndResumeAutoplay = () => {
    if (autoplayTimeout) {
      clearTimeout(autoplayTimeout)
    }

    if (swiper?.autoplay?.running) {
      swiper?.autoplay?.stop()
    }

    if (!swiper?.autoplay?.running) {
      autoplayTimeout = setTimeout(() => {
        swiper?.autoplay?.start()
      }, 30000)
    }
  }

  const isVertical = sliderDirection === SliderDirection.vertical
  const onClickPrev = () => {
    if (swiper.allowSlidePrev) {
      swiper.slidePrev()
      pauseOnTouchMove && pauseAndResumeAutoplay()
    }
  }
  const onClickNext = () => {
    if (swiper.allowSlideNext) {
      swiper.slideNext()
      pauseOnTouchMove && pauseAndResumeAutoplay()
    }
  }
  const onActiveIndexChange = (swiper: SwiperClass) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!swiper || !swiper.snapGrid) {
      return
    }
    if (!infinite) {
      const dimensionToCheck = isVertical ? swiper.height : swiper.width

      swiper.allowSlideNext =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dimensionToCheck + swiper.snapGrid[swiper.snapIndex] <
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        swiper.virtualSize
      swiper.allowSlidePrev = swiper.activeIndex > 0
    }

    if (nextArrowRef.current) {
      nextArrowRef.current.setEnabled(swiper.allowSlideNext)
    }
    if (prevArrowRef.current) {
      prevArrowRef.current.setEnabled(swiper.allowSlidePrev)
    }
  }
  return (
    <Flex
      flexDirection="column"
      position="relative"
      height="100%"
      {...rest}
      data-testid="productsSlider"
    >
      {showArrows && (
        <ProductSliderArrow
          isVertical={isVertical}
          onClick={onClickPrev}
          arrowDirection={ArrowDirection.prev}
          topPercent={arrowsTopPercent}
          bottomMargin={arrowsBottomMargin}
          fillBackground={fillArrowBackground}
          ref={prevArrowRef}
          data-testid="prevArrow"
          isHomePageBanner={isHomePageBanner}
        />
      )}
      <Box
        mx={arrowsPosition === ArrowsPosition.Outside ? 40 : 0}
        my={isVertical ? 10 : 0}
        flex="1 1 auto"
        position="relative"
      >
        <StyledBox setPositionAbsolute={isVertical}>
          <StyledSwiper
            data-testid="productSwiper"
            modules={[Autoplay, Pagination]}
            spaceBetween={spaceBetween}
            slidesPerGroup={slidesPerGroup}
            slidesPerView={slidesToShow}
            onActiveIndexChange={onActiveIndexChange}
            onTouchEnd={pauseOnTouchMove ? pauseAndResumeAutoplay : undefined}
            loop={infinite}
            autoplay={autoPlay}
            slideheight={slideHeightPercent}
            pagination={showPagination && { clickable: true }}
            onSwiper={(newSwiper) => {
              if (_onSwiper) {
                _onSwiper(newSwiper)
              }
              if (!showArrows) {
                return
              }
              setSwiper(newSwiper)
            }}
            direction={
              sliderDirection === SliderDirection.vertical
                ? 'vertical'
                : 'horizontal'
            }
            breakpoints={breakpoints}
            lazy="true"
            {...rest}
          >
            {children}
          </StyledSwiper>
        </StyledBox>
      </Box>
      {showArrows && (
        <ProductSliderArrow
          isVertical={isVertical}
          onClick={onClickNext}
          arrowDirection={ArrowDirection.next}
          topPercent={arrowsTopPercent}
          bottomMargin={arrowsBottomMargin}
          fillBackground={fillArrowBackground}
          ref={nextArrowRef}
          data-testid="nextArrow"
          isHomePageBanner={isHomePageBanner}
        />
      )}
    </Flex>
  )
}

export default ProductsSlider

const StyledSwiper = styled(Swiper)`
  height: 100%;

  .swiper-pagination-bullet {
    background: ${(props) => props.theme.colors.background};
    opacity: 1;
  }
  .swiper-pagination-bullet-active {
    background: ${(props) => props.theme.colors.onBackground};
  }
  ${(props) =>
    props.slideheight &&
    css`
      .swiper-slide {
        height: ${props.slideheight}%;
      }
    `}
`

type StyledBoxProps = {
  setPositionAbsolute: boolean
}

const absoluteCss = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const StyledBox = styled<StyledBoxProps>(Box)`
  ${(props) => props.setPositionAbsolute && absoluteCss};
`
